import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';

// ── Types ─────────────────────────────────────────────────────────────────────

type LocationState = {
  conflictingEmployee?: {
    fullName?: string | null;
  };
} | null;

// ── Component ─────────────────────────────────────────────────────────────────

export function AdminEnrollCardDuplicateErrorPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as LocationState;
  const conflictingName = state?.conflictingEmployee?.fullName ?? '—';

  return (
    <div
      style={{
        flex: 1,
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px',
      }}
    >
      <div
        className="mi-anim-slide-up"
        style={{
          flex: 1,
          width: '100%',
          maxWidth: 407,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Danger icon — same pattern as EnrollDuplicateFaceErrorPage */}
        <div className="mi-anim-check-pop" style={{ marginBottom: 23 }}>
          <div
            style={{
              width: 99,
              height: 99,
              borderRadius: '50%',
              background: `${C.danger}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 75,
                height: 75,
                borderRadius: '50%',
                background: C.danger,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Exclamation mark icon */}
              <svg
                width={33}
                height={33}
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <line x1="12" y1="8" x2="12" y2="13" />
                <circle cx="12" cy="17" r="0.5" fill="white" />
              </svg>
            </div>
          </div>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 23,
            fontWeight: 700,
            color: T.clockText,
            marginBottom: 7,
            textAlign: 'center',
          }}
        >
          {t('admin.enrollCard.duplicateTitle')}
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: 12,
            color: T.textMuted,
            textAlign: 'center',
            lineHeight: 1.7,
            marginBottom: 7,
            maxWidth: 330,
          }}
        >
          {t('admin.enrollCard.duplicateDescription')}
        </p>

        {/* Conflicting employee */}
        <p
          style={{
            fontSize: 11,
            color: T.textMuted,
            textAlign: 'center',
            lineHeight: 1.6,
            marginBottom: 30,
            maxWidth: 330,
          }}
        >
          {t('admin.enrollCard.conflictingEmployee')}: <strong style={{ color: T.text }}>{conflictingName}</strong>
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', width: '100%', maxWidth: 300 }}>
          <button
            data-testid="back-button"
            onClick={() => { navigate(-2); }}
            style={{
              width: '100%',
              padding: '11px 28px',
              borderRadius: 9,
              border: 'none',
              background: C.primary,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            {t('admin.enrollCard.backToEmployee')}
          </button>

          <button
            data-testid="retry-button"
            onClick={() => { navigate(-1); }}
            style={{
              width: '100%',
              padding: '10px 28px',
              borderRadius: 9,
              border: `1px solid ${C.blue}40`,
              background: `${C.blue}12`,
              color: C.blue,
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
            {t('admin.enrollCard.retry')}
          </button>
        </div>
      </div>
    </div>
  );
}
